/**
 * AppShell — порт untitled/project/index.html (App + Sidebar + topbar) 1:1.
 *
 * Структура прототипа:
 *   <div class="app">
 *     <aside class="sb">     — sidebar с brand / nav / quick-links / footer
 *     <main class="main">
 *       <div class="topbar"> — breadcrumb + title + meta + buttons
 *       <div class="content"> — выводит активную страницу
 */
import { useCallback, useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { heartbeat, sendOfflineBeacon } from "@/api/dashboard";
import { useTheme } from "@/lib/useTheme";

const AMO_URL   = "https://kshagin2009.amocrm.ru";
const MANGO_URL = "https://app.mango-office.ru";


interface NavItem {
  to: string;
  label: string;
  icon: string;
  /** Если задан — пункт показывается только пользователям с подходящей ролью (или is_superuser). */
  roles?: ("admin" | "head_op" | "op_manager" | "storekeeper")[];
}

const NAV: NavItem[] = [
  { to: "/",          label: "Сводка и воронка",   icon: "summary" },
  { to: "/managers",  label: "Менеджеры",          icon: "managers" },
  { to: "/callcenter",label: "Колл-центр",         icon: "phone" },
  { to: "/violations",label: "Нарушения",          icon: "warn" },
  { to: "/reports",   label: "Отчёты",             icon: "report" },
  { to: "/calls",     label: "Анализ звонков",     icon: "calls" },
  // /finance — временно скрыт из sidebar (тестирование локально с коллегой);
  // роуты остаются доступны по прямому URL для разработки.
  { to: "/settings",  label: "Настройки",          icon: "settings" },
];

// Отдельный нижний блок (под «Быстрые ссылки»)
const NAV_TOOLS: NavItem[] = [
  { to: "/chat",      label: "Чат команды",        icon: "users" },
  { to: "/visits",    label: "Расписание выездов", icon: "calendar" },
  { to: "/production",label: "Производство",       icon: "calendar" },
  { to: "/warehouse", label: "Склад",              icon: "home", roles: ["admin", "head_op", "storekeeper"] },
];

// На subdomain warehouse.* — отдельная навигация только по складу.
// Тот же bundle используется на dashboard.* / finance.* (где общая NAV),
// и на warehouse.* (где warehouse-only NAV). Различаем по hostname.
const IS_WAREHOUSE_SUBDOMAIN =
  typeof window !== "undefined" &&
  /^warehouse\./i.test(window.location.hostname);

// На warehouse-сабдомене routes лежат на корне (subdomain isolation), не под /warehouse/*
const WAREHOUSE_NAV: NavItem[] = [
  { to: "/",                   label: "Сводка",                icon: "summary"      },
  { to: "/planning",           label: "Планирование",          icon: "target"       },
  { to: "/stocks",             label: "Остатки",               icon: "report"       },
  { to: "/stocktakes",         label: "Инвентаризации",        icon: "check_circle" },
  { to: "/inflows",            label: "Приходы",               icon: "arrow_down"   },
  { to: "/purchase-orders",    label: "Заказы поставщикам",    icon: "money"        },
  { to: "/outflows",           label: "Отгрузки",              icon: "arrow_up"     },
  { to: "/sales-orders",       label: "Заказы клиентов",       icon: "send"         },
  { to: "/items",              label: "Номенклатура",          icon: "tag"          },
  { to: "/kits",               label: "Комплекты (BOM)",       icon: "archive"      },
  { to: "/contractors",        label: "Контрагенты",           icon: "users"        },
  { to: "/documents",          label: "Документы",             icon: "file"         },
];

// breadcrumb / page title — для warehouse-сабдомена (все routes на корне)
const PAGE_META: Record<string, [string, string]> = {
  "/":                      ["Склад",        "Сводка"],
  "/stocks":                ["Склад",        "Остатки"],
  "/inflows":               ["Склад",        "Приход"],
  "/inflows/history":       ["Склад",        "История приходов"],
  "/outflows":              ["Склад",        "Отгрузка"],
  "/outflows/history":      ["Склад",        "История отгрузок"],
  "/items":                 ["Склад",        "Номенклатура"],
  "/items/history":         ["Склад",        "История по позициям"],
  "/kits":                  ["Склад",        "Комплекты (BOM)"],
  "/planning":              ["Склад",        "Планирование"],
  "/purchase-orders":       ["Склад",        "Заказы поставщикам"],
  "/sales-orders":          ["Склад",        "Заказы клиентов"],
  "/stocktakes":            ["Склад",        "Инвентаризации"],
  "/contractors":           ["Склад",        "Контрагенты"],
  "/documents":             ["Склад",        "Документы"],
  "/profile":               ["Аккаунт",      "Профиль"],
  "/settings":              ["Система",      "Настройки"],
  "/health":                ["Сервис",       "Health-check"],
};

// Карточка позиции: /items/$itemId/card — динамический сегмент, не в PAGE_META
function resolvePageMeta(pathname: string): [string, string] {
  if (PAGE_META[pathname]) return PAGE_META[pathname];
  if (/^\/items\/[^/]+\/card$/.test(pathname)) return ["Склад", "Карточка позиции"];
  return ["Раздел", "Дашборд"];
}


export function WarehouseShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  async function onLogout() {
    await logout();
    void navigate({ to: "/login" });
  }

  function refreshAll() {
    void qc.invalidateQueries();
  }

  const [crumb, title] = resolvePageMeta(location.pathname);

  // ── Toast стек (глобальный — для уведомлений из notifications hook) ──
  type ToastInput = { text: string; kind?: "info" | "success" | "error"; href?: string };
  type ToastEntry = ToastInput & { id: number };
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const _pushToast = useCallback((t: ToastInput) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { ...t, id }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 8000);
  }, []);
  void _pushToast;
  const dismissToast = (id: number) => setToasts((prev) => prev.filter((x) => x.id !== id));


  // ── Heartbeat: явный пинг последний-был-в-сети раз в 45s ──
  useEffect(() => {
    if (!user) return;
    void heartbeat().catch(() => {});  // первый сразу
    const t = window.setInterval(() => { void heartbeat().catch(() => {}); }, 45000);
    return () => window.clearInterval(t);
  }, [user]);

  // ── Offline beacon ТОЛЬКО на pagehide. На visibilitychange=visible
  // вместо beacon — мгновенный heartbeat.
  useEffect(() => {
    if (!user) return;
    const onLeave = () => { sendOfflineBeacon(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void heartbeat().catch(() => {});
      }
    };
    window.addEventListener("pagehide", onLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user]);

  const isChat = location.pathname === "/chat";

  // Мобильный side-drawer: открыт/закрыт. Открывается hamburger'ом, закрывается на навигацию.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Мобильный topbar collapsed/expanded — клик раскрывает breadcrumb+кнопки
  const [topbarExpanded, setTopbarExpanded] = useState(false);
  // Сайдбар можно свернуть до узкой полосы с иконками — для широких таблиц
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("kb-sb-collapsed") === "1"; } catch { return false; }
  });
  function toggleSidebar() {
    setSidebarCollapsed(v => {
      const next = !v;
      try { localStorage.setItem("kb-sb-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }
  useEffect(() => { setTopbarExpanded(false); }, [location.pathname]);
  // Тёмная/светлая тема
  const { effective, setTheme } = useTheme();

  // Edge-swipe от левого края → "назад" (как в iOS).
  //
  // Проблема: iOS PWA standalone имеет свой native back-gesture — анимирует
  // слайд предыдущей страницы из левого края. Эта анимация ДО загрузки
  // контента показывает то, что было кэшировано — на десктопном лейауте это
  // включает sidebar, на мобильном — пустую полосу. Юзер воспринимает это
  // как "открывается сайдбар, потом подгружается страница".
  //
  // Решение: ловим touchstart с `passive: false` и preventDefault когда
  // касание в зоне ≤ 24px от левого края. Это блокирует iOS-native жест
  // и оставляет навигацию строго через React Router (плавно, без анимации
  // слайда из кэша).
  useEffect(() => {
    let startX = 0, startY = 0, active = false;
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { active = false; return; }
      const t = e.touches[0];
      // Не запускаем, если открыт лайтбокс / модалка — у них свои свайпы
      const inLightbox = !!document.querySelector(".media-lightbox, .video-lightbox, .img-upload-modal, .topic-media-modal, .file-preview-modal, .ctx-menu");
      if (inLightbox) { active = false; return; }
      if (t.clientX <= 24) {
        startX = t.clientX;
        startY = t.clientY;
        active = true;
        // Блокируем iOS-native back: иначе он анимирует слайд прошлой
        // страницы из левого края (визуально похоже на открытие сайдбара).
        // preventDefault на touchstart требует passive: false.
        try { e.preventDefault(); } catch { /* ignore */ }
      } else {
        active = false;
      }
    };
    const onEnd = (e: TouchEvent) => {
      if (!active) return;
      active = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      // Свайп вправо: dx > 80, преимущественно горизонтальный
      if (dx > 80 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        // Если в чате в режиме messages — обрабатывает ChatPage (он сам
        // подписан на touchstart/touchend и переключает activeTopicId=null).
        // НЕ делаем history.back() здесь, иначе двойной back.
        if (document.body.classList.contains("kb-in-chat-messages")) return;
        // Навигация router'а через popstate — плавно, без iOS-анимации
        if (window.history.length > 1) {
          window.history.back();
        }
      }
    };
    window.addEventListener("touchstart", onStart, { passive: false });
    window.addEventListener("touchend", onEnd, { passive: true });
    window.addEventListener("touchcancel", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return (
    <div className={`app${isChat ? " is-fullscreen" : ""}${mobileNavOpen ? " mobile-nav-open" : ""}${sidebarCollapsed ? " sb-collapsed" : ""}`}>
      {toasts.length > 0 && (
        <div className="toast-stack">
          {toasts.map((t) => {
            const inner = (
              <>
                <div className="toast-body">{t.text}</div>
                <button
                  type="button"
                  className="toast-close"
                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); dismissToast(t.id); }}
                  aria-label="Закрыть"
                >×</button>
              </>
            );
            const className = `toast toast-${t.kind === "error" ? "error" : t.kind === "success" ? "success" : "info"}`;
            return t.href ? (
              <Link
                key={t.id}
                to={t.href as "/"}
                className={className}
                style={{ textDecoration: "none", color: "inherit" }}
                onClick={() => dismissToast(t.id)}
              >
                {inner}
              </Link>
            ) : (
              <div key={t.id} className={className}>
                {inner}
              </div>
            );
          })}
        </div>
      )}
      <aside className="sb">
        <div className="sb-brand">
          <div className="sb-brand-mark">
            <div className="sb-brand-icon">К</div>
            <div>
              <div className="sb-brand-name">Кирпичные<br/>беседки</div>
            </div>
          </div>
          <div className="sb-brand-sub">{IS_WAREHOUSE_SUBDOMAIN ? "Склад" : "Дашборд отдела продаж"}</div>
        </div>

        <div>
          <div className="sb-section-label">Разделы</div>
          <div className="sb-nav">
            {(IS_WAREHOUSE_SUBDOMAIN ? WAREHOUSE_NAV : NAV).map((item) => {
              if (item.roles && !user?.is_superuser) {
                const role = (user?.role || "op_manager") as "admin" | "head_op" | "op_manager" | "storekeeper";
                if (!item.roles.includes(role)) return null;
              }
              const isActive =
                item.to === "/warehouse"
                  ? location.pathname === "/warehouse"
                  : item.to === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to as "/"}
                  className={`sb-item${isActive ? " active" : ""}`}
                >
                  <span className="sb-icon"><Icon name={item.icon} size={16}/></span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        {!IS_WAREHOUSE_SUBDOMAIN && <div className="sb-divider"/>}

        {/* Внутренние инструменты — над быстрыми ссылками. На warehouse-сабдомене скрыты. */}
        {!IS_WAREHOUSE_SUBDOMAIN && <div>
          <div className="sb-section-label">Инструменты</div>
          <div className="sb-nav">
            {NAV_TOOLS.map((item) => {
              if (item.roles && !user?.is_superuser) {
                const role = (user?.role || "op_manager") as "admin" | "head_op" | "op_manager" | "storekeeper";
                if (!item.roles.includes(role)) return null;
              }
              const isActive = location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to as "/"}
                  className={`sb-item${isActive ? " active" : ""}`}
                >
                  <span className="sb-icon"><Icon name={item.icon} size={16}/></span>
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>}

        <div>
          <div className="sb-section-label">Быстрые ссылки</div>
          <div className="sb-quick">
            <a href={AMO_URL} target="_blank" rel="noopener"><Icon name="external" size={12}/> Открыть AmoCRM</a>
            <a href={MANGO_URL} target="_blank" rel="noopener"><Icon name="phone" size={12}/> Mango Office</a>
          </div>
        </div>

        <div className="sb-footer">
          <div className="sb-user">
            <Link
              to="/profile"
              title="Профиль"
              style={{ display: "block", lineHeight: 0 }}
            >
              <Avatar
                name={user?.display_name || user?.email || "Г"}
                userId={user?.id}
                avatarFilename={user?.avatar_filename}
                color="#AB2B2B"
                size={32}
              />
            </Link>
            <Link
              to="/profile"
              className="sb-user-meta"
              style={{ flex: 1, color: "inherit", textDecoration: "none" }}
              title="Открыть профиль"
            >
              <div className="sb-user-name">{user?.display_name ?? "Геннадий"}</div>
              <div className="sb-user-role">{user?.is_superuser ? "Собственник" : "Менеджер"}</div>
            </Link>
            <button
              type="button"
              className="sb-theme-mobile"
              onClick={() => setTheme(effective === "dark" ? "light" : "dark")}
              title={effective === "dark" ? "Светлая тема" : "Тёмная тема"}
              style={{
                background: "transparent", border: "none",
                color: "rgba(246,241,232,0.7)", cursor: "pointer",
                padding: "0 4px", display: "flex", alignItems: "center",
              }}
            >
              <Icon name={effective === "dark" ? "sun" : "moon"} size={16}/>
            </button>
            <button
              type="button"
              onClick={() => void onLogout()}
              title="Выйти"
              style={{
                background: "transparent", border: "none",
                color: "rgba(246,241,232,0.6)", cursor: "pointer",
                padding: "0 4px", display: "flex", alignItems: "center",
              }}
            >
              <Icon name="logout" size={16}/>
            </button>
            <button
              type="button"
              onClick={toggleSidebar}
              title="Свернуть меню"
              className="sb-collapse-inline"
              style={{
                background: "transparent", border: "none",
                color: "rgba(246,241,232,0.6)", cursor: "pointer",
                padding: "0 4px", display: "flex", alignItems: "center",
              }}
            >
              <Icon name={"chevron_l" as "chevron_r"} size={14}/>
            </button>
          </div>
        </div>

        {/* Отдельная кнопка collapse — видна ТОЛЬКО когда сайдбар свёрнут (footer скрыт) */}
        <div className="sb-collapse-row">
          <button
            type="button"
            className="sb-collapse-btn"
            onClick={toggleSidebar}
            title="Развернуть меню"
          >
            <Icon name={"chevron_r" as "chevron_r"} size={14}/>
          </button>
        </div>
      </aside>

      {/* Мобильный overlay для закрытия sidebar */}
      <button
        type="button"
        className="sb-overlay"
        aria-label="Закрыть меню"
        onClick={() => setMobileNavOpen(false)}
      />

      <main className="main">
        <div
          className={`topbar${topbarExpanded ? " is-expanded" : " is-collapsed"}`}
          onClick={() => setTopbarExpanded((v) => !v)}
          role="button"
          tabIndex={-1}
        >
          <button
            type="button"
            className="topbar-hamburger"
            onClick={(e) => { e.stopPropagation(); setMobileNavOpen((v) => !v); }}
            aria-label="Меню"
          >
            <Icon name="filter" size={18}/>
          </button>
          <div className="tb-left">
            <div>
              <div className="tb-crumb">{crumb}</div>
              <h1 className="tb-title">{title}</h1>
            </div>
          </div>
          <div className="tb-right" onClick={(e) => e.stopPropagation()}>
            <div className="tb-meta">
              <span className="tb-pulse"/>
              <span>обновлено только что</span>
            </div>
            <button
              className="btn btn-sm tb-theme-btn"
              onClick={() => setTheme(effective === "dark" ? "light" : "dark")}
              title={effective === "dark" ? "Светлая тема" : "Тёмная тема"}
            >
              <Icon name={effective === "dark" ? "sun" : "moon"} size={14}/>
            </button>
            <button className="btn btn-sm" onClick={refreshAll}><Icon name="refresh" size={12}/> Обновить</button>
            <a className="btn btn-sm btn-primary" href={AMO_URL} target="_blank" rel="noopener"><Icon name="external" size={12}/> AmoCRM</a>
          </div>
        </div>

        <div className={`content${isChat ? " is-fullscreen-page" : ""}`}>
          <Outlet/>
        </div>
      </main>
    </div>
  );
}

